import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as authApi from '../api/auth';
import styles from './AuthPages.module.css';

/**
 * ForgotPasswordPage — unauthenticated password reset request.
 *
 * State 'request': email input → calls POST /auth/reset-password (existing endpoint).
 * State 'sent':    confirmation view with envelope illustration + "Back to Login".
 *
 * Figma: node 2432-11365, screens Reset-PW-1 and Reset-PW-2.
 */
export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [state, setState] = useState('request'); // 'request' | 'sent'

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await authApi.resetPassword(email);
      // Backend always returns 200 (non-enumeration) — transition on any success
      setState('sent');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main className={styles.page}>
      {/* Left panel — illustration + logo */}
      <div className={styles.authLeft}>
        <img src="/assets/auth-illustration.webp" alt="" className={styles.bgImage} />
        <img src="/assets/wanderwords-logo.svg" alt="WanderWords" className={styles.logo} />
      </div>

      {/* Right panel — form + footer */}
      <div className={styles.authRight}>
        <div className={styles.content}>

        {/* ── State: request ── */}
        {state === 'request' && (
          <>
            {error && (
              <div className={styles.errorBanner} role="alert">{error}</div>
            )}

            <h1 className={styles.cardTitle}>Forgot Password?</h1>

            <form id="forgot-form" onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label htmlFor="forgot-email" className={styles.label}>
                  {t('auth.email')}
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={styles.input}
                />
              </div>
            </form>

            <div className={styles.actions}>
              <button
                type="submit"
                form="forgot-form"
                disabled={isLoading}
                className={styles.submitBtn}
              >
                {isLoading ? 'Sending…' : 'Request Reset Link'}
              </button>
              <p className={styles.switchPrompt}>
                Already have an account?{' '}
                <Link to="/login" className={styles.switchLink}>Log In</Link>
              </p>
            </div>
          </>
        )}

        {/* ── State: sent ── */}
        {state === 'sent' && (
          <>
            <h1 className={styles.cardTitle}>Email Sent</h1>

            <div className={styles.cardIllustration}>
              <img
                src="/assets/email-sent-illustration.webp"
                alt=""
                aria-hidden="true"
                style={{ transform: 'rotate(-17.4deg)', width: '120px' }}
              />
            </div>

            <p className={styles.switchPrompt}>
              We&#8217;ve sent a link to reset your password to <strong>{email}</strong>.
            </p>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={() => navigate('/login')}
              >
                Back to Login
              </button>
            </div>
          </>
        )}
      </div>

      {/* Page footer */}
      <footer className={styles.pageFooter}>
        <a href="https://wanderwords.co/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>
          Terms of Service
        </a>
        <a href="https://wanderwords.co/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>
          Privacy Policy
        </a>
        <p className={styles.footerCopyright}>&copy; 2026 Bricksquare Pte. Ltd.</p>
        </footer>
      </div>
    </main>
  );
}
